/**
 * The wallet contract, pinned in one file.
 *
 * ADR-0012 (`docs/ADR/ADR-0012-wallet-ledger.md` in the backend repo) — read
 * directly off `src/modules/wallet/{wallet.controller.ts,wallet.service.ts,dto/*}`
 * in that worktree, which at the time of writing was unmerged. Every shape below
 * is copied from there, not guessed, so a late change to the backend contract is
 * a diff to this one file rather than a hunt through every screen that uses it.
 *
 * THE ONE RULE: a balance is a projection of an immutable log, never a mutable
 * number. Nothing in this file or the screens that import it ever sums a ledger
 * client-side to show a balance — `account.balance` and `ledgerBalance` are both
 * numbers the SERVER computed, and every amount here is the Decimal *string* the
 * API sent, formatted for display and never parsed back to a float.
 */
import { WalletAccountKind, WalletAccountStatus, WalletTransactionType } from '@/lib/shared-types';

export { WalletAccountKind, WalletAccountStatus, WalletTransactionType };

/** `GET /wallet`, `GET /wallet/:id` (redacted — `codeHash` never leaves the server). */
export interface WalletAccount {
  id: string;
  tenantId: string;
  branchId: string | null;
  kind: WalletAccountKind;
  status: WalletAccountStatus;
  customerId: string | null;
  /** Last 4 characters of the code. Null for STORE_CREDIT, which has no code. */
  codeLast4: string | null;
  /** The projection — a Decimal string. Never sum the ledger to recompute this. */
  balance: string;
  /** What was on it at issuance, kept for breakage reporting. */
  issuedValue: string;
  currency: string;
  reloadable: boolean;
  transferable: boolean;
  expiresAt: string | null;
  activatedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only `GET /wallet` (the list) includes this — `GET /wallet/:id` does not. */
  customer?: { id: string; name: string | null; phone: string | null } | null;
}

/** One append-only ledger line. Never updated, never deleted — see ADR-0012 Decision 1. */
export interface WalletTransaction {
  id: string;
  accountId: string;
  type: WalletTransactionType;
  /** SIGNED Decimal string. Positive adds value, negative removes it. */
  amount: string;
  currency: string;
  /** The projection as at this line — a running balance with no client-side sum. */
  balanceAfter: string;
  reason: string;
  refType: string | null;
  refId: string | null;
  /** The line this one reverses (REFUND/VOID only). Unique on the backend. */
  reversesId: string | null;
  branchId: string | null;
  actorId: string | null;
  idempotencyKey: string;
  createdAt: string;
}

/** `GET /wallet/:id` — the account plus its recent statement and the ledger sum beside the cache. */
export interface WalletAccountDetail extends WalletAccount {
  /** Recomputed from the ledger. If it disagrees with `balance`, the cache is wrong — see ADR-0012. */
  ledgerBalance: string;
  recentTransactions: WalletTransaction[];
}

/** What every money-moving route (`issue`, `reload`, `redeem`, `reverse`, `adjust`) answers with. */
export interface WalletPosting {
  transaction: WalletTransaction;
  account: WalletAccount;
  ledgerBalance: string;
  /** Set when a replayed idempotency key was recognised rather than acted on again. */
  replayed?: true;
}

/** `POST /wallet/gift-cards` — `code` is on the FIRST response only; a replay omits it. */
export interface IssuedGiftCard extends WalletPosting {
  code?: string;
}

// ------------------------------------------------------------------- request bodies

/** `POST /wallet/gift-cards` — `IssueGiftCardDto`. */
export interface IssueGiftCardBody {
  branchId: string;
  amount: string;
  customerId?: string;
  activate?: boolean;
  reloadable?: boolean;
  transferable?: boolean;
  expiresAt?: string;
  note?: string;
  idempotencyKey: string;
}

/** `POST /wallet/store-credit` — `IssueStoreCreditDto`. */
export interface IssueStoreCreditBody {
  customerId: string;
  branchId: string;
  amount: string;
  reason: string;
  refType?: string;
  refId?: string;
  expiresAt?: string;
  idempotencyKey: string;
}

/** `POST /wallet/:id/reload` — `ReloadWalletDto`. */
export interface ReloadWalletBody {
  amount: string;
  reason?: string;
  idempotencyKey: string;
}

/**
 * `POST /wallet/:id/adjust` — `AdjustWalletDto`. `amount` is SIGNED: negative
 * removes value, positive adds it. `reason` is mandatory on the DTO — it IS the
 * control, per ADR-0012 Decision 8.
 */
export interface AdjustWalletBody {
  amount: string;
  reason: string;
  idempotencyKey: string;
}

/** `POST /wallet/transactions/:id/reverse` — `ReverseWalletDto`. No amount: it is worth exactly what the original was. */
export interface ReverseWalletBody {
  reason?: string;
  idempotencyKey: string;
}

/** `POST /wallet/:id/transfer` — `TransferWalletDto`. Moves no money, writes no ledger line. */
export interface TransferWalletBody {
  customerId: string;
}

/** `POST /wallet/:id/close` — body is `ReverseWalletDto`; only `reason` is read by the service. */
export interface CloseWalletBody {
  reason?: string;
  idempotencyKey: string;
}

// ------------------------------------------------------------------------- helpers

/** Surface the API's own message rather than a generic failure line. */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

/**
 * Types that may be reversed at all, mirroring `wallet-ledger.ts#isReversible`
 * on the backend. The server is the authority — a stale local computation just
 * decides whether to offer the button; a wrong guess here is a 400/409 with a
 * readable message, never a silent double-refund.
 */
export function isReversible(type: WalletTransactionType): boolean {
  return (
    type === WalletTransactionType.REDEEM ||
    type === WalletTransactionType.RELOAD ||
    type === WalletTransactionType.ISSUE
  );
}

/** Whether some other line in `lines` already reverses this one. Same caveat as `isReversible`. */
export function alreadyReversed(lines: WalletTransaction[], transactionId: string): boolean {
  return lines.some((line) => line.reversesId === transactionId);
}
